import { useRef } from "react";
import { Input, InputNumber, Tooltip } from "antd";
import { DeleteOutlined, PlusOutlined, HolderOutlined } from "@ant-design/icons";

/**
 * Small building blocks for the Proposal & Quotation Content editor.
 *
 * Two things they are careful about:
 *  - HTML fields are edited as raw source, because the copy carries <b>/<i> that
 *    a plain input would escape into visible tags.
 *  - Lists reorder by drag: the order IS the value (steps are sequential, terms
 *    are numbered), so it has to be directly editable.
 */

export const PLACEHOLDERS = ["{{client_name}}", "{{contact_name}}", "{{marks}}", "{{brand_name}}", "{{plan}}"];

export const label = {
  fontSize: 11.5, fontWeight: 700, color: "#6b7280", display: "block", marginBottom: 5,
};
export const hintStyle = { fontSize: 11, color: "#9ca3af", marginTop: 5, lineHeight: 1.5 };

/** Shown under any field whose text is interpolated at render time. */
export const PlaceholderHint = () => (
  <div style={hintStyle}>
    Auto-replaced in documents: {PLACEHOLDERS.map((p) => (
      <code key={p} style={{ background: "#f5f3ff", color: "#5b48e8", borderRadius: 4, padding: "1px 5px", marginRight: 4, fontSize: 10.5 }}>
        {p}
      </code>
    ))}
  </div>
);

export const HtmlHint = () => (
  <div style={hintStyle}>
    HTML allowed — <code style={{ color: "#5b48e8" }}>&lt;b&gt;</code> and{" "}
    <code style={{ color: "#5b48e8" }}>&lt;i&gt;</code> render as formatting.
  </div>
);

export const TextField = ({ title, value, onChange, editable, placeholder }) => (
  <div>
    <label style={label}>{title}</label>
    {editable ? (
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    ) : (
      <div style={{ fontSize: 14, color: value ? "#111827" : "#d1d5db", wordBreak: "break-word" }}>
        {value || "—"}
      </div>
    )}
  </div>
);

export const NumField = ({ title, value, onChange, editable, suffix }) => (
  <div>
    <label style={label}>{title}</label>
    {editable ? (
      <InputNumber value={value ?? null} onChange={onChange} min={0} style={{ width: "100%" }} />
    ) : (
      <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
        {value ?? "—"}{value != null && suffix ? ` ${suffix}` : ""}
      </div>
    )}
  </div>
);

/**
 * A rich-text field edited as source.
 *
 * A contentEditable would look friendlier but silently rewrites the markup the
 * copy depends on, so the source stays the editor and the read view renders it.
 */
export const HtmlField = ({ title, value, onChange, editable, rows = 4, showPlaceholders = true }) => (
  <div>
    <label style={label}>{title}</label>
    {editable ? (
      <>
        <Input.TextArea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          spellCheck={false}
          style={{ fontFamily: "monospace", fontSize: 12.5, lineHeight: 1.6 }}
        />
        <HtmlHint />
        {showPlaceholders && <PlaceholderHint />}
      </>
    ) : (
      <div
        style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}
        dangerouslySetInnerHTML={{ __html: value || "<span style='color:#d1d5db'>—</span>" }}
      />
    )}
  </div>
);

/**
 * An ordered list of rows, reorderable by drag.
 *
 * `fields` describes one row; `blank` is what "Add" inserts. Rows render in the
 * stored order and dragging rewrites that order, because for steps and numbered
 * terms the order carries meaning.
 */
export const ListEditor = ({
  items = [], onChange, editable, fields, blank, addLabel = "Add row", numbered,
}) => {
  const dragIndex = useRef(null);

  const move = (from, to) => {
    if (from === to || from == null || to == null) return;
    const next = [...items];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    onChange(next);
  };

  const setField = (idx, key, value) =>
    onChange(items.map((row, i) => (i === idx ? (key === null ? value : { ...row, [key]: value }) : row)));

  const rowStyle = {
    display: "flex", gap: 12, alignItems: "flex-start", padding: "12px 0",
    borderTop: "1px solid #f1f5f9",
  };

  return (
    <div>
      {items.length === 0 && (
        <div style={{ fontSize: 13, color: "#9ca3af", padding: "10px 0" }}>
          Nothing here yet{editable ? " — add the first row below." : "."}
        </div>
      )}

      {items.map((row, i) => (
        <div
          key={i}
          style={rowStyle}
          draggable={editable}
          onDragStart={() => { dragIndex.current = i; }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => { move(dragIndex.current, i); dragIndex.current = null; }}
        >
          {editable && (
            <Tooltip title="Drag to reorder">
              <span style={{ cursor: "grab", color: "#c7cbe0", fontSize: 16, paddingTop: 6, flexShrink: 0 }}>
                <HolderOutlined />
              </span>
            </Tooltip>
          )}
          {numbered && (
            <span style={{ width: 22, flexShrink: 0, paddingTop: 8, fontSize: 12, fontWeight: 700, color: "#9ca3af" }}>
              {i + 1}.
            </span>
          )}

          <div style={{ flex: 1, display: "grid", gap: 12, gridTemplateColumns: fields.length > 1 ? "repeat(auto-fit,minmax(180px,1fr))" : "1fr", minWidth: 0 }}>
            {fields.map((f) => {
              /* A bare string list (common_includes) has no key — the row itself
                 is the value. */
              const value = f.key === null ? row : row?.[f.key];
              const onFieldChange = (v) => setField(i, f.key, v);
              if (f.type === "html") {
                return (
                  <div key={f.key || "value"} style={{ gridColumn: "1/-1" }}>
                    <HtmlField
                      title={f.title} value={value} onChange={onFieldChange}
                      editable={editable} rows={f.rows || 3}
                      showPlaceholders={f.placeholders !== false}
                    />
                  </div>
                );
              }
              return (
                <TextField
                  key={f.key || "value"} title={f.title} value={value}
                  onChange={onFieldChange} editable={editable} placeholder={f.placeholder}
                />
              );
            })}
          </div>

          {editable && (
            <Tooltip title="Remove">
              <button
                onClick={() => onChange(items.filter((_, x) => x !== i))}
                style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #fecaca", background: "#fef2f2", color: "#dc2626", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0, marginTop: 4 }}
              >
                <DeleteOutlined />
              </button>
            </Tooltip>
          )}
        </div>
      ))}

      {editable && (
        <div style={{ paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
          <button
            onClick={() => onChange([...items, typeof blank === "function" ? blank() : blank])}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, border: "1px dashed #c7d2fe", background: "#f5f3ff", color: "#5b48e8", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
          >
            <PlusOutlined /> {addLabel}
          </button>
        </div>
      )}
    </div>
  );
};
